import type { SessionCategory } from './scheduledSessionStorage';
import { authService } from '../../../utils/authService';

export const LIVE_SESSION_CATEGORY_PREFIX: Record<SessionCategory, string> = {
  mandatory: 'LCM',
  workshop_online: 'LW-Online',
  workshop_onsite: 'LW-Onsite',
  optional: 'LCO',
};

/** Example: LCM <subject_id> - 001 - 15M 2026 (also accepts legacy LC/LM/LO) */
const NAME_PATTERN =
  /^(LCM|LCO|LC|LM|LO|LW-Online|LW-Onsite|LW)\s+(\S+)\s+-\s+(\d{3})\s+-\s+(\d{1,2})M\s+(\d{4})$/i;

const MONTH_NAME_TO_NUM: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

export function deriveSubjectCode(subjectNameOrId: string): string {
  const raw = String(subjectNameOrId || '').trim();
  if (!raw || raw.toLowerCase() === 'session') return 'SESSION';
  // Session titles use subject_id (sanitized, always UPPERCASE).
  const cleaned = raw.replace(/[^A-Za-z0-9_-]/g, '').toUpperCase();
  return cleaned || 'SESSION';
}

/** Fallback only — prefer course name period. */
export function formatLiveSessionPeriod(startDate: string): string {
  const [year, month] = startDate.split('-');
  if (!year || !month) return '';
  return `${Number(month)}M ${year}`;
}

/**
 * Parse month/year from course name.
 * Supports: "7M 2026", "Jul 2026", "July 2026", "07/2026", "7-2026".
 */
export function formatPeriodFromCourseName(courseName: string): string {
  const text = String(courseName || '').trim();
  if (!text) return '';

  const nm = text.match(/(?<!\d)(\d{1,2})\s*M\s*(\d{4})(?!\d)/i);
  if (nm) {
    const month = Number(nm[1]);
    if (month >= 1 && month <= 12) return `${month}M ${nm[2]}`;
  }

  const named = text.match(
    /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[\s\-/]+(\d{4})\b/i,
  );
  if (named) {
    const month = MONTH_NAME_TO_NUM[named[1].toLowerCase()];
    if (month) return `${month}M ${named[2]}`;
  }

  const slash = text.match(/(?<!\d)(\d{1,2})\s*[/\-]\s*(\d{4})(?!\d)/);
  if (slash) {
    const month = Number(slash[1]);
    if (month >= 1 && month <= 12) return `${month}M ${slash[2]}`;
  }

  return '';
}

export function parseDurationMonthsFromCourseName(courseName: string): number | null {
  // e.g. "15-Month Tech Program", "12 months Full Stack"
  const match = String(courseName || '').match(/(?<!\d)(\d{1,3})\s*[-–—]?\s*months?\b/i);
  if (!match) return null;
  const months = Number(match[1]);
  return months >= 1 && months <= 120 ? months : null;
}

function toDateParts(value?: string | Date | null): { year: number; month: number } | null {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { year: value.getFullYear(), month: value.getMonth() + 1 };
  }
  const text = String(value).trim();
  const m = text.match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) };
}

/**
 * '{totalMonths}M {startYear}' from course start → end (inclusive months).
 */
export function formatPeriodFromCourseSpan(
  courseStart?: string | Date | null,
  courseEnd?: string | Date | null,
  durationMonths?: number | null,
): string {
  const start = toDateParts(courseStart);
  if (!start) return '';

  let months =
    typeof durationMonths === 'number' && durationMonths > 0
      ? Math.floor(durationMonths)
      : null;

  if (months == null) {
    const end = toDateParts(courseEnd) || start;
    let startY = start.year;
    let startM = start.month;
    let endY = end.year;
    let endM = end.month;
    if (endY < startY || (endY === startY && endM < startM)) {
      [startY, startM, endY, endM] = [endY, endM, startY, startM];
    }
    months = (endY - startY) * 12 + (endM - startM) + 1;
  }

  return `${Math.max(1, months)}M ${start.year}`;
}

export function resolveSessionPeriod(
  courseName?: string,
  startDate?: string,
  courseStart?: string | Date | null,
  courseEnd?: string | Date | null,
): string {
  const fromName = formatPeriodFromCourseName(courseName || '');
  if (fromName) return fromName;

  const durationMonths = parseDurationMonthsFromCourseName(courseName || '');
  const fromSpan = formatPeriodFromCourseSpan(courseStart, courseEnd, durationMonths);
  if (fromSpan) return fromSpan;

  if (durationMonths && startDate) {
    const parts = toDateParts(startDate);
    if (parts) return `${durationMonths}M ${parts.year}`;
  }

  return formatLiveSessionPeriod(startDate || '');
}

export interface GenerateLiveSessionNameParams {
  category: SessionCategory;
  subjectName: string;
  startDate: string;
  courseName?: string;
  /** Existing titles in the same batch (or course) scope */
  existingNames?: string[];
}

function prefixesEquivalent(a: string, b: string): boolean {
  // ---------------------------------------------------------------
  // step 1: Treat legacy LC/LM as LCM and LO as LCO for uniqueness
  // ---------------------------------------------------------------
  const norm = (p: string) => {
    const u = String(p || '').trim().toUpperCase();
    if (u === 'LC' || u === 'LM' || u === 'LCM') return 'LCM';
    if (u === 'LO' || u === 'LCO') return 'LCO';
    return u;
  };
  return norm(a) === norm(b);
}

function collectUsedNumbers(
  existingNames: string[],
  prefix: string,
  period: string,
  subjectCode?: string,
): number[] {
  const used = new Set<number>();
  const subjectU = String(subjectCode || '').trim().toUpperCase();
  for (const name of existingNames) {
    const match = String(name || '').trim().match(NAME_PATTERN);
    if (!match) continue;
    const [, existingPrefix, existingSubject, seq, month, year] = match;
    const existingPeriod = `${Number(month)}M ${year}`;
    if (
      prefixesEquivalent(existingPrefix, prefix) &&
      existingPeriod === period &&
      (!subjectU || String(existingSubject || '').toUpperCase() === subjectU)
    ) {
      used.add(Number.parseInt(seq, 10) || 0);
    }
  }
  return Array.from(used).filter((n) => n > 0).sort((a, b) => a - b);
}

function nextAvailableNumber(used: number[]): number {
  const set = new Set(used);
  let seq = 1;
  while (set.has(seq)) seq += 1;
  return seq;
}

/**
 * Course-plan template title (no batch period).
 * Example: `LCM SQL - 001`
 * At batch create, use {@link instantiatePlannedSessionName}.
 */
export function buildCoursePlanSessionTitle(
  category: SessionCategory,
  subjectIdOrName: string,
  sequence: number,
): string {
  const prefix = LIVE_SESSION_CATEGORY_PREFIX[category] || 'LCM';
  const code = deriveSubjectCode(subjectIdOrName);
  const seq = String(Math.max(1, sequence)).padStart(3, '0');
  return `${prefix} ${code} - ${seq}`;
}

/**
 * Stable key for looking up a planned draft when creating per batch.
 * Example: `lcm:sql:001`
 */
export function buildCoursePlanSessionKey(
  category: SessionCategory,
  subjectIdOrName: string,
  sequence: number,
): string {
  const prefix = (LIVE_SESSION_CATEGORY_PREFIX[category] || 'LCM')
    .toLowerCase()
    .replace(/\s+/g, '-');
  const code = deriveSubjectCode(subjectIdOrName).toLowerCase();
  const seq = String(Math.max(1, sequence)).padStart(3, '0');
  return `${prefix}:${code}:${seq}`;
}

/**
 * Instantiate a course-plan template for a specific batch/period.
 * `LCM SQL - 001` + `7M 2026` → `LCM SQL - 001 - 7M 2026`
 */
export function instantiatePlannedSessionName(
  plannedTitle: string,
  period: string,
): string {
  const title = String(plannedTitle || '').trim();
  const p = String(period || '').trim();
  if (!title) return p ? `LCM SESSION - 001 - ${p}` : 'LCM SESSION - 001';
  if (!p) return title;
  if (title.endsWith(` - ${p}`)) return title;
  return `${title} - ${p}`;
}

/**
 * Sequence increments by session type (prefix) + course month/year
 * (parsed from course name).
 */
export function generateLiveSessionName(params: GenerateLiveSessionNameParams): string {
  const prefix = LIVE_SESSION_CATEGORY_PREFIX[params.category];
  const subjectCode = deriveSubjectCode(params.subjectName);
  const period = resolveSessionPeriod(params.courseName, params.startDate);
  if (!prefix || !period) {
    return `${prefix || 'LCM'} ${subjectCode} - 001`;
  }

  const used = collectUsedNumbers(
    params.existingNames ?? [],
    prefix,
    period,
    subjectCode,
  );
  const nextSeq = nextAvailableNumber(used);
  return `${prefix} ${subjectCode} - ${String(nextSeq).padStart(3, '0')} - ${period}`;
}

export type SessionNameAvailability = {
  isTaken: boolean;
  exactNameTaken: boolean;
  numberTaken: boolean;
  requestedName: string | null;
  requestedNumber: number | null;
  availableNumber: number;
  availableNumberPadded: string;
  suggestedSessionName: string;
  prefix: string;
  period: string;
  subjectCode: string;
  usedNumbers: number[];
  existingTitles: string[];
};

export type CheckSessionNameParams = {
  category: SessionCategory;
  subjectName: string;
  startDate: string;
  batchId?: string;
  courseId?: string;
  courseName?: string;
  sessionName?: string;
  sessionProvider?: 'google_meet' | 'livekit';
  localExistingNames?: string[];
};

/**
 * Check whether a session name/number is taken for the same session type +
 * course month/year (from course name). Returns available number + suggested name.
 */
export async function checkSessionNameAvailability(
  params: CheckSessionNameParams,
): Promise<SessionNameAvailability> {
  const prefix = LIVE_SESSION_CATEGORY_PREFIX[params.category];
  const subjectCode = deriveSubjectCode(params.subjectName);
  const period = resolveSessionPeriod(params.courseName, params.startDate);
  const localFallback = (): SessionNameAvailability => {
    const used = collectUsedNumbers(
      params.localExistingNames ?? [],
      prefix,
      period,
      subjectCode,
    );
    const availableNumber = nextAvailableNumber(used);
    return {
      isTaken: false,
      exactNameTaken: false,
      numberTaken: false,
      requestedName: params.sessionName?.trim() || null,
      requestedNumber: null,
      availableNumber,
      availableNumberPadded: String(availableNumber).padStart(3, '0'),
      suggestedSessionName: `${prefix} ${subjectCode} - ${String(availableNumber).padStart(3, '0')} - ${period}`,
      prefix,
      period,
      subjectCode,
      usedNumbers: used,
      existingTitles: params.localExistingNames ?? [],
    };
  };

  const batchId = (params.batchId || '').trim();
  const courseId = (params.courseId || '').trim();
  if (!batchId && !courseId) {
    return localFallback();
  }

  try {
    const response = await authService.post('/session/codelive/check-session-name/', {
      category: params.category,
      start_date: params.startDate,
      subject_name: params.subjectName || 'Session',
      session_name: params.sessionName || null,
      batch_id: batchId || null,
      course_id: courseId || null,
      course_name: params.courseName || null,
      session_provider: params.sessionProvider || 'livekit',
    });
    const data = response?.data?.data;
    if (!response?.data?.success || !data) {
      return localFallback();
    }

    const existingTitles: string[] = [
      ...(Array.isArray(data.existing_titles) ? data.existing_titles : []),
      ...(params.localExistingNames ?? []),
    ];
    const resolvedPeriod = String(data.period || period);
    const resolvedSubject = deriveSubjectCode(
      String(data.subject_code || subjectCode),
    );
    const used = collectUsedNumbers(
      existingTitles,
      String(data.prefix || prefix),
      resolvedPeriod,
      resolvedSubject,
    );
    const availableNumber = nextAvailableNumber(used);
    const suggestedSessionName = `${data.prefix || prefix} ${resolvedSubject} - ${String(availableNumber).padStart(3, '0')} - ${resolvedPeriod}`;

    return {
      isTaken: Boolean(data.is_taken),
      exactNameTaken: Boolean(data.exact_name_taken),
      numberTaken: Boolean(data.number_taken),
      requestedName: data.requested_name ?? params.sessionName?.trim() ?? null,
      requestedNumber:
        typeof data.requested_number === 'number' ? data.requested_number : null,
      availableNumber,
      availableNumberPadded: String(availableNumber).padStart(3, '0'),
      suggestedSessionName,
      prefix: data.prefix || prefix,
      period: resolvedPeriod,
      subjectCode: resolvedSubject,
      usedNumbers: used,
      existingTitles,
    };
  } catch (err) {
    console.error('Failed to check session name availability', err);
    return localFallback();
  }
}

export type ResolveLiveSessionNameParams = {
  category: SessionCategory;
  subjectName: string;
  startDate: string;
  batchId?: string;
  courseId?: string;
  courseName?: string;
  localExistingNames?: string[];
  sessionName?: string;
  sessionProvider?: 'google_meet' | 'livekit';
};

export async function resolveLiveSessionName(
  params: ResolveLiveSessionNameParams,
): Promise<string> {
  const result = await checkSessionNameAvailability(params);
  return result.suggestedSessionName;
}

export const LIVE_SESSION_NAMING_CONVENTION =
  'LCM = Live Class Mandatory · LCO = Live Class Optional · LW-Online = Online Workshop · LW-Onsite = Onsite Workshop';
